import { styled } from '@mui/material';
import { useMatches } from '@tanstack/react-router';
import { type ReactNode, useCallback, useState } from 'react';
import { IncidentBanner } from '../Common/IncidentBanner';
import { DASHBOARD_SIDEBAR_WIDTH, DashboardAppBar, DashboardAppBarInner } from './DashboardAppBar';
import { DashboardSidebar } from './DashboardSidebar';

export const DASHBOARD_LAYOUT_GAP = 64;
export const DASHBOARD_LAYOUT_GAP_PX = `${DASHBOARD_LAYOUT_GAP}px`;

const DashboardContent = styled('div')`
  display: flex;
  flex: 1 1 auto;
  max-width: 100%;
  padding-top: ${DASHBOARD_LAYOUT_GAP_PX};
  min-height: 100vh;
`;

const DashboardContentInner = styled('div')`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
`;

/**
 * Each authenticated dashboard page should be wrapped in this component.
 * It provides the layout and the sidebar. An error boundary is also provided for the children.
 */
export const Dashboard = ({ children }: { children: ReactNode }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const handleSidebarOpen = useCallback(() => {
    setSidebarOpen(true);
  }, []);
  const handleSidebarClose = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const childMatchesNoDashboardRoute = useChildMatchesNoDashboardRoute();
  if (childMatchesNoDashboardRoute) {
    return children;
  }

  return (
    <>
      <DashboardContent sx={{ marginLeft: { lg: DASHBOARD_SIDEBAR_WIDTH } }}>
        <DashboardContentInner>{children}</DashboardContentInner>
      </DashboardContent>
      <DashboardAppBar onMenuClick={handleSidebarOpen} />
      <DashboardSidebar onClose={handleSidebarClose} open={isSidebarOpen} />
    </>
  );
};

export const DashboardOutline = ({ children }: { children?: ReactNode }) => (
  <DashboardContent>
    <DashboardContentInner>{children}</DashboardContentInner>
    <DashboardAppBarInner sx={{ pl: { lg: 0 } }}>
      <IncidentBanner />
    </DashboardAppBarInner>
  </DashboardContent>
);

function useChildMatchesNoDashboardRoute() {
  return useMatches({
    select: (matches) =>
      matches.find(
        (match) =>
          match.fullPath === '/accounts/$accountId/clusters/$clusterId/scale' ||
          match.fullPath === '/accounts/$accountId/clusters/create' ||
          match.fullPath === '/accounts/$accountId/serverless-spaces/$spaceId/create-collection' ||
          match.fullPath === '/accounts/$accountId/hybrid-cloud/create' ||
          match.fullPath === '/accounts/$accountId/hybrid-cloud/$hybridCloudEnvId/onboarding/connection',
      ),
  });
}
